import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";

/* ---------------- Types ---------------- */

export type TransactionRule = {
  id: string;
  field: "description" | "account" | "category";
  operator: "contains" | "equals";
  value: string;
  caseSensitive: boolean;
  categoryId: string;
  categoryName: string;
};

type RulePayload = Omit<TransactionRule, 'id' | 'categoryName'>;
type RuleWithScope = RulePayload & { onlyUncategorized: boolean };

type Props = {
  open: boolean;
  onClose: () => void;
  rules: TransactionRule[];
  onCreateRule: (rule: RulePayload) => Promise<void> | void;
  onUpdateRule: (id: string, rule: RulePayload) => Promise<void> | void;
  onDeleteRule: (id: string) => Promise<void> | void;
  onPreviewRule: (rule: RuleWithScope) => Promise<{ count: number; sample: Array<{ id: string; occurredAt: string; description: string; accountName: string; amountCents: number; currency: string; categoryName: string | null }> }>;
  onApplyRule: (rule: RuleWithScope) => Promise<{ updatedCount: number }>;
  availableCategories: Array<{ id: string; name: string }>;
  availableAccounts: string[];
};

/* ---------------- Component ---------------- */

export default function TransactionRulesDialog({
  open,
  onClose,
  rules,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  onPreviewRule,
  onApplyRule,
  availableCategories,
}: Props) {
  const [newRule, setNewRule] = useState<Omit<TransactionRule, "id" | 'categoryName'>>({
    field: "description",
    operator: "contains",
    value: "",
    caseSensitive: false,
    categoryId: "",
  });

  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<RulePayload>({
    field: "description",
    operator: "contains",
    value: "",
    caseSensitive: false,
    categoryId: "",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [onlyUncategorized, setOnlyUncategorized] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [preview, setPreview] = useState<{ count: number; sample: Array<{ id: string; occurredAt: string; description: string; accountName: string; amountCents: number; currency: string; categoryName: string | null }> } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const addRule = () => {
    if (!newRule.value || !newRule.categoryId) return;
    onCreateRule(newRule);
    setNewRule({ field: "description", operator: "contains", value: "", caseSensitive: false, categoryId: "" });
    setPreview(null);
    setPreviewError(null);
  };

  const startEdit = (r: TransactionRule) => {
    setEditingRuleId(r.id);
    setEditingRule({
      field: r.field,
      operator: r.operator,
      value: r.value,
      caseSensitive: r.caseSensitive,
      categoryId: r.categoryId,
    });
    setPreview(null);
    setPreviewError(null);
  };

  const cancelEdit = () => {
    setEditingRuleId(null);
  };

  const saveEdit = async () => {
    if (!editingRuleId || !editingRule.value || !editingRule.categoryId) return;
    setEditSaving(true);
    try {
      await onUpdateRule(editingRuleId, editingRule);
      setEditingRuleId(null);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Failed to update rule');
    } finally {
      setEditSaving(false);
    }
  };

  const previewRule = async (rule: RulePayload, uncategorizedOnly = onlyUncategorized) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await onPreviewRule({ ...rule, onlyUncategorized: uncategorizedOnly });
      setPreview(result);
    } catch (e) {
      setPreview(null);
      setPreviewError(e instanceof Error ? e.message : 'Failed to preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyRule = async (rule: RulePayload, uncategorizedOnly = onlyUncategorized) => {
    setApplyLoading(true);
    setPreviewError(null);
    try {
      const result = await onApplyRule({ ...rule, onlyUncategorized: uncategorizedOnly });
      setPreview({ count: result.updatedCount, sample: [] });
    } catch (e) {
      setPreview(null);
      setPreviewError(e instanceof Error ? e.message : 'Failed to apply');
    } finally {
      setApplyLoading(false);
    }
  };

  const runPreview = async () => {
    if (!newRule.value || !newRule.categoryId) return;
    await previewRule({ ...newRule, categoryId: newRule.categoryId });
  };

  const saveAndApply = async () => {
    if (!newRule.value || !newRule.categoryId) return;
    setApplyLoading(true);
    try {
      await onCreateRule(newRule);
      const result = await onApplyRule({ ...newRule, categoryId: newRule.categoryId, onlyUncategorized });
      setPreview({ count: result.updatedCount, sample: [] });
      setNewRule({ field: "description", operator: "contains", value: "", caseSensitive: false, categoryId: "" });
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Failed to apply');
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Transaction Categorization Rules</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Automatically categorize transactions based on keywords or account matches.
        </Typography>

        {/* Existing Rules List */}
        {rules.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No rules defined yet.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {rules.map((r) =>
              editingRuleId === r.id ? (
                /* ──── Inline edit form ──── */
                <Box
                  key={r.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    p: 1.5,
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Select
                        size="small"
                        value={editingRule.field}
                        onChange={(e) => setEditingRule((prev) => ({ ...prev, field: e.target.value as any }))}
                        sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}
                      >
                        <MenuItem value="description">Description</MenuItem>
                        <MenuItem value="account">Account</MenuItem>
                        <MenuItem value="category">Category</MenuItem>
                      </Select>
                      <Select
                        size="small"
                        value={editingRule.operator}
                        onChange={(e) => setEditingRule((prev) => ({ ...prev, operator: e.target.value as any }))}
                        sx={{ width: { xs: '100%', sm: 130 }, flexShrink: 0 }}
                      >
                        <MenuItem value="contains">contains</MenuItem>
                        <MenuItem value="equals">equals</MenuItem>
                      </Select>
                      <TextField
                        size="small"
                        value={editingRule.value}
                        onChange={(e) => setEditingRule((prev) => ({ ...prev, value: e.target.value }))}
                        sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 200 } }}
                      />
                      <Select
                        size="small"
                        displayEmpty
                        value={editingRule.categoryId}
                        onChange={(e) => setEditingRule((prev) => ({ ...prev, categoryId: String(e.target.value) }))}
                        sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 200 } }}
                      >
                        <MenuItem value="">
                          <em>Select Category</em>
                        </MenuItem>
                        {availableCategories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <FormControlLabel
                        sx={{ userSelect: 'none' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={editingRule.caseSensitive}
                            onChange={(e) => setEditingRule((prev) => ({ ...prev, caseSensitive: e.target.checked }))}
                          />
                        }
                        label={<Typography variant="body2">Case sensitive</Typography>}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={cancelEdit}
                          disabled={editSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={saveEdit}
                          disabled={editSaving || !editingRule.value || !editingRule.categoryId}
                        >
                          {editSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              ) : (
                /* ──── Read-only row ──── */
                <Box
                  key={r.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {r.field} {r.operator} <strong>"{r.value}"</strong>
                    {!r.caseSensitive && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}(case-insensitive)
                      </Typography>
                    )}
                    {' '}→ {r.categoryName}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => previewRule({ field: r.field, operator: r.operator, value: r.value, caseSensitive: r.caseSensitive, categoryId: r.categoryId })}
                    disabled={previewLoading}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => applyRule({ field: r.field, operator: r.operator, value: r.value, caseSensitive: r.caseSensitive, categoryId: r.categoryId })}
                    disabled={applyLoading}
                  >
                    Apply
                  </Button>
                  <IconButton size="small" onClick={() => startEdit(r)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => onDeleteRule(r.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            )}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Add New Rule Form */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add New Rule
        </Typography>

        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: 'wrap' }}
          >
            <Select
              size="small"
              value={newRule.field}
              onChange={(e) => setNewRule((r) => ({ ...r, field: e.target.value as any }))}
              sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
            >
              <MenuItem value="description">Description</MenuItem>
              <MenuItem value="account">Account</MenuItem>
              <MenuItem value="category">Category</MenuItem>
            </Select>

            <Select
              size="small"
              value={newRule.operator}
              onChange={(e) => setNewRule((r) => ({ ...r, operator: e.target.value as any }))}
              sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}
            >
              <MenuItem value="contains">contains</MenuItem>
              <MenuItem value="equals">equals</MenuItem>
            </Select>

            <TextField
              size="small"
              placeholder="Value"
              value={newRule.value}
              onChange={(e) => setNewRule((r) => ({ ...r, value: e.target.value }))}
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 260 } }}
            />

            <Select
              size="small"
              displayEmpty
              value={newRule.categoryId}
              onChange={(e) => setNewRule((r) => ({ ...r, categoryId: String(e.target.value) }))}
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 240 } }}
            >
              <MenuItem value="">
                <em>Select Category</em>
              </MenuItem>
              {availableCategories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <FormControlLabel
              sx={{ userSelect: 'none' }}
              control={
                <Checkbox
                  size="small"
                  checked={newRule.caseSensitive}
                  onChange={(e) => setNewRule((r) => ({ ...r, caseSensitive: e.target.checked }))}
                />
              }
              label={<Typography variant="body2">Case sensitive</Typography>}
            />
            <FormControlLabel
              sx={{ mr: 'auto', userSelect: 'none' }}
              control={
                <Checkbox
                  size="small"
                  checked={!onlyUncategorized}
                  onChange={(e) => setOnlyUncategorized(!e.target.checked)}
                />
              }
              label={<Typography variant="body2">Include already-categorized</Typography>}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={runPreview}
              disabled={previewLoading || !newRule.value || !newRule.categoryId}
            >
              {previewLoading ? 'Previewing…' : 'Preview'}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={saveAndApply}
              disabled={applyLoading || !newRule.value || !newRule.categoryId}
            >
              {applyLoading ? 'Applying…' : 'Save + Apply'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={addRule}
              disabled={!newRule.value || !newRule.categoryId}
              startIcon={<AddIcon />}
            >
              Save only
            </Button>
          </Stack>
        </Stack>

        {(previewError || preview) && (
          <Box sx={{ mt: 2 }}>
            {previewError && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {previewError}
              </Typography>
            )}

            {preview && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Matching transactions: <strong>{preview.count}</strong>
                </Typography>
                {preview.sample.length > 0 ? (
                  <Stack spacing={0.5}>
                    {preview.sample.slice(0, 10).map((t) => (
                      <Typography key={t.id} variant="caption" sx={{ display: 'block' }}>
                        {new Date(t.occurredAt).toISOString().slice(0, 10)} — {t.accountName} — {(t.amountCents / 100).toFixed(2)} {t.currency} — {t.description}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No sample rows returned.
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
